import { useRouter } from 'next/compat/router';
import { useQuery } from '@apollo/client';

import { GetCitiesWithDispensariesDocument } from 'src/gql/graphql';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useProduct from 'hooks/use-product';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { Crumb } from './bread-crumbs.types';
import {
  getCrumbs,
  removeUnsupportedCities,
  getFormattedCityName,
  getCityWithDispensary,
  getState,
  getLocationUrl,
} from './bread-crumbs.helpers';

type UseGetLocationHrefParams = {
  needsLocationCrumb: boolean;
  dispensaryCity: string;
  dispensaryState: string;
};

type UseBreadCrumbsProps = {
  lastCrumbLabel?: string;
};

type UseBreadCrumbsReturn = {
  crumbs: Crumb[];
};

function useGetLocationHref({
  needsLocationCrumb,
  dispensaryCity,
  dispensaryState,
}: UseGetLocationHrefParams): string | null {
  // Only fire (and only bother computing) the location crumb when we're actually going to
  // render one - i.e. skip it on pages with no dispensary in context (home, /dispensaries
  // listing, etc.) and on white-label surfaces (embedded-menu/kiosks/stores/plus/etc.) where
  // this breadcrumb is never shown. The request itself is already CDN-cached, so there's no
  // need for an additional client-side (localStorage) cache on top of that.
  const hasDispensaryLocation = Boolean(dispensaryCity && dispensaryState) && needsLocationCrumb;

  const { data, loading } = useQuery(GetCitiesWithDispensariesDocument, {
    skip: !hasDispensaryLocation,
  });

  if (!hasDispensaryLocation || !data || loading) {
    return null;
  }
  const cleanedCities = removeUnsupportedCities(data);
  const formattedCityName = getFormattedCityName(dispensaryCity);
  const didFindCity = !!getCityWithDispensary(cleanedCities, dispensaryState, formattedCityName);
  const currentState = getState(cleanedCities, dispensaryState);

  const locationHref =
    didFindCity && currentState
      ? getLocationUrl(currentState.country ?? 'US', currentState.name ?? dispensaryState, formattedCityName)
      : null;

  return locationHref;
}

export function useBreadCrumbs({ lastCrumbLabel }: UseBreadCrumbsProps): UseBreadCrumbsReturn {
  const { route, query } = useRouter() ?? {};
  const isMobile = useIsMobile();
  const UI = useUI();
  const { dispensary } = useDispensary();
  const { product } = useProduct();
  const { categories } = useDispensaryCategories();

  const dispensaryCity = dispensary?.location?.city ?? '';
  const dispensaryState = dispensary?.location?.state ?? '';
  const dispensaryName = dispensary?.name ?? '';
  const dispensaryCName = dispensary?.cName ?? '';

  // The location ('Dispensaries > City') crumb only ever appears on the main dutchie.com
  // marketplace - never on white-label surfaces (embedded-menu, kiosks, stores, plus, etc, all
  // captured by `UI.isDutchieMain`) and never on the product detail page (which builds its own
  // crumb trail via buildProductDetailPageCrumbs and never reads locationHref). Reusing these
  // existing UI variant checks keeps this in lockstep with how the app already knows which
  // surface it's rendering, instead of re-deriving it from a hardcoded route list here.
  const needsLocationCrumb = UI.isDutchieMain && !UI.isProductDetailPage;

  const locationHref = useGetLocationHref({ needsLocationCrumb, dispensaryCity, dispensaryState });

  const dispensaryInfo = {
    cName: dispensaryCName,
    name: dispensaryName,
    city: dispensaryCity,
    state: dispensaryState,
    locationHref,
  };

  const crumbs = getCrumbs({ route: route ?? '', query: query ?? {}, dispensaryInfo, isMobile, product, categories });

  if (lastCrumbLabel) {
    crumbs.push({
      label: lastCrumbLabel,
    });
  }

  return {
    crumbs,
  };
}
